/**
 * Typed API client for the Kambi balance endpoints.
 */
import type { ApiClient } from '../api/client.ts';

export interface KambiAuthResponse {
  readonly authenticated: boolean;
  readonly expiresAt: string;
  readonly shopCount: number;
}

export interface KambiSessionStatus {
  readonly active: boolean;
  readonly expiresAt?: string;
  readonly shopCount?: number;
}

export interface KambiShopSummary {
  readonly ref: string;
  readonly name: string;
  readonly status: string;
  readonly address: string | null;
}

export interface TerminalBalance {
  readonly balance: number | null;
  readonly currency: string;
  readonly lastDeposit: number | null;
  readonly lastDepositTime: string | null;
  readonly additionalDeposits: number;
  readonly betsReceived: number;
  readonly payouts: number;
  readonly withdrawals: number;
  readonly cashDrops: number;
  readonly vouchersPaid: number;
  readonly note: string | null;
}

export interface KioskBalanceInfo {
  readonly terminalRef: string;
  readonly name: string;
  readonly balance: {
    readonly balance: number;
    readonly currency: string;
    readonly denominations: readonly { readonly denomination: number; readonly count: number }[];
    readonly timestamp: string;
  } | null;
  readonly error: string | null;
}

export interface ShopBalances {
  readonly shopName: string;
  readonly shopRef: string;
  readonly otcBalances: Readonly<Record<string, TerminalBalance>>;
  readonly kioskBalances: readonly KioskBalanceInfo[];
  readonly fetchedAt: string;
}

export interface ShopBalanceSummary {
  readonly shopRef: string;
  readonly shopName: string;
  readonly address: string | null;
  readonly otcTotal: number;
  readonly otcTerminalCount: number;
  readonly otcWithBalance: number;
  readonly kioskTotal: number;
  readonly kioskCount: number;
  readonly totalCash: number;
}

export interface BalancesSummary {
  readonly shops: readonly ShopBalanceSummary[];
  readonly totals: {
    readonly grandTotal: number;
    readonly otcTotal: number;
    readonly kioskTotal: number;
    readonly shopCount: number;
  };
  readonly fetchedAt: string;
}

export interface BalancesApi {
  readonly authenticate: (
    email: string,
    password: string,
    totpCode: string,
    csrfToken: string,
  ) => Promise<KambiAuthResponse>;
  readonly getSession: () => Promise<KambiSessionStatus>;
  readonly getShops: () => Promise<readonly KambiShopSummary[]>;
  readonly getShopBalances: (shopRef: string) => Promise<ShopBalances>;
  readonly getSummary: () => Promise<BalancesSummary>;
}

export const createBalancesApi = (client: ApiClient): BalancesApi => ({
  authenticate: async (email, password, totpCode, csrfToken) => {
    const result = await client.send(
      'POST',
      '/api/admin/kambi/authenticate',
      {
        // We'll parse the response ourselves since we don't have a zod schema for this
        safeParse: (value: unknown) => ({ success: true as const, data: value }),
      } as never,
      {
        body: { email, password, totpCode },
        csrfToken,
      },
    );
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as KambiAuthResponse;
  },
  getSession: async () => {
    const result = await client.get('/api/admin/kambi/session', {
      safeParse: (value: unknown) => ({ success: true as const, data: value }),
    } as never);
    if (!result.ok) return { active: false };
    return result.data as unknown as KambiSessionStatus;
  },
  getShops: async () => {
    const result = await client.get('/api/admin/kambi/shops', {
      safeParse: (value: unknown) => ({ success: true as const, data: value }),
    } as never);
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as readonly KambiShopSummary[];
  },
  getShopBalances: async (shopRef) => {
    const result = await client.get(`/api/admin/kambi/shops/${shopRef}/balances`, {
      safeParse: (value: unknown) => ({ success: true as const, data: value }),
    } as never);
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as ShopBalances;
  },
  getSummary: async () => {
    const result = await client.get('/api/admin/kambi/balances/summary', {
      safeParse: (value: unknown) => ({ success: true as const, data: value }),
    } as never);
    if (!result.ok) throw new Error(result.failure.message);
    return result.data as unknown as BalancesSummary;
  },
});
